import React, { useEffect } from "react";

import { Field, useFormikContext } from "formik";

import { useGetYardiPropertyReasons } from "manager/hooks/api";
import { YARDI_REASON_TYPES } from "manager/pages/Marketplace/integrations/yardi/shared";
import { Select, TextArea } from "shared/components/Form";
import Icon from "shared/components/Icon";
import {
  VERO_CANCEL_REASON_SOMETHING_ELSE,
  VERO_CANCEL_REASONS,
} from "shared/config/veroCancelReasons";

import { DealSummaryWithProperty } from "../types";

import {
  applicantFirstName,
  unfinishedIntroCopy,
  warningCopy,
} from "./copy";
import {
  FieldBlock,
  FieldLabel,
  Helper,
  HelperWarn,
  Intro,
  NoteArea,
  NoteBlock,
  OptionalMark,
  RequiredMark,
  UnavailableYardiField,
  WarningIconWrap,
  WarningRow,
  WarningText,
  YardiBanner,
  YardiBannerIcon,
  YardiBannerText,
} from "./styled";

type FormValues = {
  veroCancelReason?: string;
  veroCancelNote?: string;
  extraParams?: { reason?: number };
};

interface Props {
  deal: DealSummaryWithProperty;
  yardiConnected: boolean;
  identityCheckUnfinished: boolean;
  hideVeroNote?: boolean;
}

export const SentilinkCancelFields: React.FC<Props> = ({
  deal,
  yardiConnected,
  identityCheckUnfinished,
  hideVeroNote = false,
}) => {
  const { values, touched, errors, setFieldValue, setFieldTouched, validateForm } =
    useFormikContext<FormValues>();
  const firstName = applicantFirstName(deal?.applicantsSummary);
  const propertyName = deal?.property?.name;
  const yardiPropertyId = deal?.property?.yardiPropertyId;

  const {
    yardiPropertyReasons = [],
    isLoading: yardiReasonsLoading,
    isError: yardiReasonsError,
  } = useGetYardiPropertyReasons(yardiPropertyId, YARDI_REASON_TYPES.CANCEL, {
    suppressErrorToast: true,
  });

  const yardiCodesAvailable =
    yardiConnected &&
    !yardiReasonsLoading &&
    !yardiReasonsError &&
    yardiPropertyReasons.length > 0;
  const yardiUnavailable =
    yardiConnected && !yardiReasonsLoading && !yardiCodesAvailable;

  const noteRequired =
    values?.veroCancelReason === VERO_CANCEL_REASON_SOMETHING_ELSE;

  const yardiPlaceholder = yardiReasonsError
    ? "No codes available"
    : "No codes configured in Yardi";

  useEffect(() => {
    if (!yardiConnected || yardiReasonsLoading) {
      return;
    }
    validateForm();
  }, [yardiConnected, yardiReasonsLoading, yardiCodesAvailable, validateForm]);

  useEffect(() => {
    if (!hideVeroNote) {
      return;
    }
    setFieldValue("veroCancelNote", undefined);
  }, [hideVeroNote, setFieldValue]);

  return (
    <>
      {identityCheckUnfinished && (
        <Intro>{unfinishedIntroCopy({ firstName, yardiConnected })}</Intro>
      )}

      {yardiConnected && (
        <>
          <YardiBanner>
            <YardiBannerIcon>
              <Icon.DefaultLogoIcon aria-hidden="true" />
            </YardiBannerIcon>
            <YardiBannerText>
              {propertyName ? (
                <>
                  <strong>{propertyName}</strong> is synced with{" "}
                  <strong>Yardi</strong>. Its cancellation codes come from Yardi.
                </>
              ) : (
                <>
                  This property is synced with <strong>Yardi</strong>. Its
                  cancellation codes come from Yardi.
                </>
              )}
            </YardiBannerText>
          </YardiBanner>

          <FieldBlock>
            <FieldLabel htmlFor="extraParams.reason">
              Yardi cancellation code
              {yardiCodesAvailable && (
                <>
                  {" "}
                  <RequiredMark>Required</RequiredMark>
                </>
              )}
            </FieldLabel>
            <UnavailableYardiField
              className={yardiUnavailable ? "yardi-unavailable" : undefined}
            >
              <Field
                as={Select}
                name="extraParams.reason"
                id="extraParams.reason"
                placeholder={yardiUnavailable ? yardiPlaceholder : undefined}
                disabled={!yardiCodesAvailable || yardiReasonsLoading}
                loading={yardiReasonsLoading}
                data-testid="yardi-cancel-reason-select"
                error={
                  touched?.extraParams?.reason &&
                  (errors?.extraParams as { reason?: string } | undefined)
                    ?.reason
                }
                validate={
                  yardiConnected && yardiReasonsLoading
                    ? () => "Loading Yardi reasons"
                    : yardiCodesAvailable
                      ? (value: number) =>
                          value
                            ? undefined
                            : "Yardi cancellation code is required"
                      : undefined
                }
                onChange={(value: number) =>
                  setFieldValue("extraParams.reason", value)
                }
                onBlur={() => setFieldTouched("extraParams.reason", true)}
              >
                {yardiPropertyReasons.map((option, index) => (
                  <Select.Option
                    key={option.id}
                    value={option.id}
                    data-testid={`yardi-reason-option-${index}`}
                  >
                    {option.value}
                  </Select.Option>
                ))}
              </Field>
            </UnavailableYardiField>
            {yardiCodesAvailable && (
              <Helper>
                This is the code Yardi will record. VERO does not add to,
                reorder or rename this list.
              </Helper>
            )}
            {yardiReasonsError && (
              <HelperWarn>
                We couldn't reach Yardi for this property. You can still cancel
                in VERO — record the cancellation in Yardi yourself.
              </HelperWarn>
            )}
          </FieldBlock>
        </>
      )}

      <FieldBlock>
        <FieldLabel htmlFor="veroCancelReason">
          {yardiConnected ? "VERO reason" : "Why are you cancelling it?"}{" "}
          <RequiredMark>Required</RequiredMark>
        </FieldLabel>
        <Field
          as={Select}
          name="veroCancelReason"
          id="veroCancelReason"
          data-testid="vero-cancel-reason-select"
          error={touched?.veroCancelReason && errors?.veroCancelReason}
          onChange={(value: string) => setFieldValue("veroCancelReason", value)}
          onBlur={() => setFieldTouched("veroCancelReason", true)}
        >
          {VERO_CANCEL_REASONS.map((option) => (
            <Select.Option key={option} value={option}>
              {option}
            </Select.Option>
          ))}
        </Field>
        {yardiConnected && (
          <Helper>
            Stays in VERO for your own reporting. This is the reason that
            actually explains the cancellation — the Yardi list has no code for
            a failed identity check.
          </Helper>
        )}
      </FieldBlock>

      {!hideVeroNote && (
        <NoteBlock>
          <FieldLabel htmlFor="veroCancelNote">
            Note{" "}
            {!noteRequired && <OptionalMark>(optional)</OptionalMark>}
            {noteRequired && (
              <>
                {" "}
                <RequiredMark>Required</RequiredMark>
              </>
            )}
          </FieldLabel>
          <NoteArea>
            <Field
              as={TextArea}
              id="veroCancelNote"
              name="veroCancelNote"
              rows={3}
              placeholder="Anything the next person should know"
              error={touched?.veroCancelNote && errors?.veroCancelNote}
            />
          </NoteArea>
        </NoteBlock>
      )}

      <WarningRow>
        <WarningIconWrap>
          <Icon.OrangeExclamation aria-hidden="true" width={40} height={40} />
        </WarningIconWrap>
        <WarningText>{warningCopy({ firstName })}</WarningText>
      </WarningRow>
    </>
  );
};
